import { useEffect, useState } from 'react';
import { Workbox } from 'workbox-window';
import {
  Snackbar,
  Alert,
  Button,
  Box,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

/**
 * ServiceWorkerUpdate Component
 * 
 * Manages service worker lifecycle and prompts users to reload when updates are available.
 * Integrates with workbox-window to detect new service worker versions and handle updates.
 * Supports theme-aware styling using CSS custom properties for consistent appearance
 * across light and dark modes.
 * 
 * Features:
 * - Detects when a new service worker is waiting to activate
 * - Displays a user-friendly update prompt using MUI Snackbar
 * - Handles skipWaiting message to activate new service worker
 * - Automatically reloads page after update activation
 * - Provides manual reload option for users
 * - Theme-aware notification colors using CSS variables
 */
const ServiceWorkerUpdate = () => {
  const [showUpdatePrompt, setShowUpdatePrompt] = useState(false);
  const [workboxInstance, setWorkboxInstance] = useState<Workbox | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    // Only register service worker in production and if supported
    if (
      import.meta.env.PROD &&
      'serviceWorker' in navigator &&
      window.workbox !== undefined
    ) {
      const wb = new Workbox('/sw.js');

      // Event: Service worker is waiting to activate
      wb.addEventListener('waiting', () => {
        setShowUpdatePrompt(true);
      });

      // Event: Service worker has been activated
      wb.addEventListener('controlling', () => {
        // Reload page to load new content from updated service worker
        window.location.reload();
      });

      // Event: Service worker has been installed for the first time
      wb.addEventListener('installed', (event) => {
        if (!event.isUpdate) {
          // First time installation - no update prompt needed
          console.log('Service worker installed for the first time');
        }
      });

      // Event: Service worker activation failed
      wb.addEventListener('redundant', () => {
        console.warn('Service worker became redundant');
      });

      // Register the service worker
      wb.register()
        .then(() => {
          console.log('Service worker registered successfully');
        })
        .catch((error) => {
          console.error('Service worker registration failed:', error);
        });

      setWorkboxInstance(wb);

      // Cleanup on unmount
      return () => {
        // Workbox doesn't provide unregister method, but we can clear the instance
        setWorkboxInstance(null);
      };
    }
  }, []);

  /**
   * Handles the update action when user clicks the reload button
   * Sends skipWaiting message to the waiting service worker
   */
  const handleUpdate = () => {
    if (workboxInstance) {
      setIsUpdating(true);
      
      // Send skipWaiting message to the waiting service worker
      workboxInstance.messageSkipWaiting();
      
      // The 'controlling' event will trigger page reload
      // If it doesn't trigger within 3 seconds, reload manually
      setTimeout(() => {
        if (isUpdating) {
          window.location.reload();
        }
      }, 3000);
    }
  };

  /**
   * Handles dismissing the update prompt
   * User can continue using the current version
   */
  const handleDismiss = () => {
    setShowUpdatePrompt(false);
  };

  return (
    <Snackbar
      open={showUpdatePrompt}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      sx={{ bottom: { xs: 80, sm: 24 } }}
    >
      <Alert
        severity="info"
        variant="filled"
        sx={{
          width: '100%',
          alignItems: 'center',
          backgroundColor: 'var(--color-info, #0288d1)',
          color: 'var(--color-info-contrast, #ffffff)',
          '& .MuiAlert-message': {
            flex: 1,
          },
          '& .MuiAlert-icon': {
            color: 'var(--color-info-contrast, #ffffff)',
          },
        }}
        action={
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              color="inherit"
              size="small"
              onClick={handleDismiss}
              disabled={isUpdating}
              sx={{
                color: 'var(--color-info-contrast, #ffffff)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              Later
            </Button>
            <Button
              color="inherit"
              size="small"
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleUpdate}
              disabled={isUpdating}
              sx={{
                color: 'var(--color-info-contrast, #ffffff)',
                borderColor: 'rgba(255, 255, 255, 0.5)',
                '&:hover': {
                  borderColor: 'rgba(255, 255, 255, 0.8)',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
                '&.Mui-disabled': {
                  color: 'rgba(255, 255, 255, 0.5)',
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
              }}
            >
              {isUpdating ? 'Updating...' : 'Reload'}
            </Button>
          </Box>
        }
      >
        <Typography variant="body2" component="div">
          <strong>New version available!</strong>
          <br />
          Reload to get the latest features and improvements.
        </Typography>
      </Alert>
    </Snackbar>
  );
};

export default ServiceWorkerUpdate;